import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Navigate,
  useLocation,
} from 'react-router-dom';
import './index.css';

import App from './App';
import Signup from './pages/Signup';
import Signin from './pages/Signin';
import Dashboard from './pages/Dashboard';
import Properties from './pages/Properties';
import AddProperty from './pages/AddProperty';
import Property from './pages/Property';
import Contacts from './pages/Contacts';
import AddContact from './pages/AddContact';
import Contact from './pages/Contact';
import EditContact from './pages/EditContact';
import EditProperty from './pages/EditProperty';
import Tasks from './pages/Tasks';
import RootLayout from './layouts/RootLayout';
import MarketingLayout from './layouts/MarketingLayout';
import Documents from './pages/Documents';
import Settings from './pages/Settings';
import Analytics from './pages/Analytics';
import Payments from './pages/Payments';
import TenantPortal from './pages/TenantPortal';
import TenantAppointmentResponse from './pages/TenantAppointmentResponse';
import ContractorJobPortal from './pages/ContractorJobPortal';
import SigninTenant from './pages/TenantLogin';
import TenantSignup from './pages/TenantSignup';
import PayRent from './pages/PayRent';
import PaymentsSuccess from './pages/PaymentsSuccess';
import PaymentsCancel from './pages/PaymentsCancel';
import ProtectedRoute from './components/ProtectedRoute';
import AutopayComplete from './pages/AutopayComplete';
import StripePayouts from './pages/StripePayouts';
import Waitlist from './pages/Waitlist';
import WaitlistSuccess from './pages/WaitlistSuccess';
import StripeOnboardingComplete from './pages/StripeOnboardingComplete';
import StripeOnboardingRefresh from './pages/StripeOnboardingRefresh';
import Teams from './pages/Teams';
import JoinTeam from './pages/JoinTeam';
import PrivacyPolicy from './pages/Privacy';
import TermsService from './pages/TermsService';
import CookiePolicy from './pages/CookiePolicy';
import NotFound from './pages/NotFound';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

import { AuthProvider } from './context/AuthContext';

function LegacyJobsRedirect() {
  const location = useLocation();
  return <Navigate to={`/tasks${location.search || ''}`} replace />;
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route element={<MarketingLayout />}>
        <Route path='/' element={<App />} />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<RootLayout />}>
          <Route path='/dashboard' element={<Dashboard />} />
          <Route path='/properties' element={<Properties />} />
          <Route path='/add-property' element={<AddProperty />} />
          <Route path='/property' element={<Navigate to="/properties" replace />} />
          <Route path='/property/:propertyId' element={<Property />} />
          <Route path='/contacts' element={<Contacts />} />
          <Route path='/contact/:id' element={<Contact />} />
          <Route path='/edit-contact/:id' element={<EditContact />} />
          <Route path='/edit-property/:id' element={<EditProperty />} />
          <Route path='/add-contact' element={<AddContact />} />
          <Route path='/tasks' element={<Tasks />} />
          <Route path='/jobs' element={<LegacyJobsRedirect />} />
          <Route path='/documents' element={<Documents />} />
          <Route path='/settings' element={<Settings />} />
          <Route path='/analytics' element={<Analytics />} />
          <Route path='/payments' element={<Payments />} />
          <Route path='/stripe-payouts' element={<StripePayouts />} />
          <Route path='/teams' element={<Teams />} />
          <Route path='/teams/join' element={<JoinTeam />} />
        </Route>
      </Route>

      <Route path="/stripe/onboarding/complete" element={<StripeOnboardingComplete />} />
      <Route path="/stripe/onboarding/refresh" element={<StripeOnboardingRefresh />} />

      <Route path='/tenant-portal' element={<TenantPortal />} />
      <Route path='/tenant-appointments/:token' element={<TenantAppointmentResponse />} />
      <Route path='/jobs/:token' element={<ContractorJobPortal />} />
      <Route path='/tenant-sign-in' element={<SigninTenant />} />
      <Route path='/tenant-sign-up' element={<Navigate to="/tenant-signup" replace />} />
      <Route path='/tenant-signup' element={<TenantSignup />} /> 
      <Route path='/pay-rent' element={<PayRent />} />
      <Route path="/autopay-complete" element={<AutopayComplete />} />
      <Route path="/payments/success" element={<PaymentsSuccess />} />
      <Route path="/payments/cancel" element={<PaymentsCancel />} />

      <Route path='/sign-in' element={<Signin />} />
      <Route path='/sign-up' element={<Signup />} />
      <Route path='/forgot-password' element={<ForgotPassword />} />
      <Route path='/reset-password' element={<ResetPassword />} />
      <Route path='/waitlist' element={<Waitlist />} />
      <Route path='/waitlist-success' element={<WaitlistSuccess />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsService />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="*" element={<NotFound />} />
    </>
  )
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  </React.StrictMode>
);
